// Checking off a reminder or deal next step (Goals spec Phase 0, Brett 2026-10-22).
// A call or meeting becomes one activity on the timeline. Then a small panel: tap the call's
// outcome, add a note, fix the date if it happened earlier, and set the next task (every outcome
// suggests one). Undo puts everything back. Also used after logging a call on the contact page,
// to complete a matching open reminder instead of counting the call twice.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { ACTIVITY, ACTIVITY_TYPES, defaultNote, FOLLOW_UP_WHEN, OUTCOMES, suggestedFollowUp, type ActivityType, type Outcome } from '../lib/activity';
import { supabase, unwrap } from '../lib/supabase';
import { useToast } from './Toast';
import { Button, cx, ErrorBox, Input, Modal, Textarea } from './ui';

interface Who {
  contactId: string | null;
  dealId: string | null;
  /** Person (or deal) name for titles and the follow-up text. */
  name: string;
}

type Panel =
  | { kind: 'reminder'; reminderId: string; type: ActivityType; interactionId: string | null; who: Who }
  | { kind: 'step'; auditId: string; type: ActivityType; interactionId: string | null; who: Who }
  | { kind: 'logged'; interactionId: string; type: ActivityType | 'other'; who: Who; openReminders: { id: string; note: string | null }[] };

export interface CheckOffReminder {
  id: string;
  activity_type: string | null;
  contactId: string | null;
  dealId: string | null;
  name: string;
}
export interface CheckOffStep {
  dealId: string;
  type: string | null;
  name: string;
}

function rpcError(e: { message: string } | null) {
  if (e) throw new Error(e.message);
}

interface CheckOffApi {
  checkOffReminder: (r: CheckOffReminder) => void;
  checkOffStep: (s: CheckOffStep) => void;
  /** After logging an activity on the contact page: offer to complete an open reminder and set the next task. */
  afterLogged: (p: Omit<Extract<Panel, { kind: 'logged' }>, 'kind'>) => void;
}
const CheckOffContext = createContext<CheckOffApi | null>(null);

/** Check-off actions. The panel lives in CheckOffProvider (app level), not in the list that opened it:
 *  ticking the last item empties and unmounts that list, which used to close the panel at once. */
export function useCheckOff(): CheckOffApi {
  const api = useContext(CheckOffContext);
  if (!api) throw new Error('useCheckOff needs CheckOffProvider');
  return api;
}

export function CheckOffProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [panel, setPanel] = useState<Panel | null>(null);
  const refresh = () => qc.invalidateQueries();

  const reminder = useMutation({
    mutationFn: async (r: CheckOffReminder) => {
      const { data, error } = await supabase.rpc('complete_reminder', { p_reminder: r.id });
      rpcError(error);
      return { r, interactionId: (data as string | null) ?? null };
    },
    onSuccess: ({ r, interactionId }) => {
      refresh();
      const who = { contactId: r.contactId, dealId: r.dealId, name: r.name };
      if (r.activity_type) setPanel({ kind: 'reminder', reminderId: r.id, type: r.activity_type as ActivityType, interactionId, who });
      else
        toast({
          message: 'Done.',
          autoHideMs: 8000,
          action: { label: 'Undo', onClick: () => void supabase.rpc('uncomplete_reminder', { p_reminder: r.id }).then(refresh) },
        });
    },
    onError: (e) => toast({ message: `Couldn't check that off: ${(e as Error).message}`, autoHideMs: 8000 }),
  });

  const step = useMutation({
    mutationFn: async (s: CheckOffStep) => {
      const { data, error } = await supabase.rpc('complete_next_step', { p_deal: s.dealId });
      rpcError(error);
      return { s, res: data as { interaction_id: string | null; audit_id: string; contact_id: string | null } };
    },
    onSuccess: ({ s, res }) => {
      refresh();
      const who = { contactId: res.contact_id, dealId: s.dealId, name: s.name };
      if (s.type) setPanel({ kind: 'step', auditId: res.audit_id, type: s.type as ActivityType, interactionId: res.interaction_id, who });
      else
        toast({
          message: 'Next step done.',
          autoHideMs: 8000,
          action: { label: 'Undo', onClick: () => void supabase.rpc('undo_next_step', { p_audit: res.audit_id }).then(refresh) },
        });
    },
    onError: (e) => toast({ message: `Couldn't check that off: ${(e as Error).message}`, autoHideMs: 8000 }),
  });

  const api: CheckOffApi = {
    checkOffReminder: (r) => reminder.mutate(r),
    checkOffStep: (s) => step.mutate(s),
    afterLogged: (p) => setPanel({ kind: 'logged', ...p }),
  };
  return (
    <CheckOffContext.Provider value={api}>
      {children}
      {panel && <AfterPanel key={JSON.stringify(panel)} panel={panel} onClose={() => setPanel(null)} onChanged={refresh} />}
    </CheckOffContext.Provider>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cx('rounded-full border px-3 py-1 text-sm', on ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300')}
    >
      {children}
    </button>
  );
}

const localNow = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

function AfterPanel({ panel, onClose, onChanged }: { panel: Panel; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const isCall = panel.type === 'call';
  const hasActivity = panel.kind !== 'logged' && !!panel.interactionId;
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [note, setNote] = useState('');
  const [when, setWhen] = useState(localNow);
  const [linkTo, setLinkTo] = useState<string | null>(panel.kind === 'logged' && panel.openReminders.length === 1 ? panel.openReminders[0]!.id : null);
  // Next task: suggested from the outcome until the user picks something themselves.
  const [touched, setTouched] = useState(false);
  const initial = panel.kind === 'logged' || panel.type !== 'email' ? suggestedFollowUp(null) : null;
  const [nextType, setNextType] = useState<ActivityType | null>(initial?.type ?? null);
  const [nextDays, setNextDays] = useState(initial?.days ?? 7);
  const [nextDate, setNextDate] = useState('');
  const [nextNote, setNextNote] = useState('');

  const pickOutcome = (o: Outcome) => {
    const next = outcome === o ? null : o;
    setOutcome(next);
    if (!touched) {
      const s = suggestedFollowUp(next);
      setNextType(s?.type ?? null);
      if (s) setNextDays(s.days);
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      if (panel.kind !== 'logged' && panel.interactionId) {
        const text = note.trim();
        unwrap(
          await supabase
            .from('interactions')
            .update({
              outcome: isCall ? outcome : null,
              occurred_at: new Date(when).toISOString(),
              ...(text ? { body_text: text, snippet: text.slice(0, 200) } : {}),
            })
            .eq('id', panel.interactionId),
        );
      }
      if (panel.kind === 'logged' && linkTo) {
        const { error } = await supabase.rpc('complete_reminder', { p_reminder: linkTo, p_interaction: panel.interactionId });
        rpcError(error);
      }
      if (nextType) {
        const due = nextDate || FOLLOW_UP_WHEN.find((w) => w.days === nextDays)?.date() || FOLLOW_UP_WHEN[2]!.date();
        unwrap(
          await supabase.from('reminders').insert({
            contact_id: panel.who.contactId,
            deal_id: panel.who.dealId,
            due_date: due,
            note: nextNote.trim() || defaultNote(nextType, panel.who.name),
            reminder_type: 'manual_follow_up',
            activity_type: nextType,
          }),
        );
      }
      return nextType;
    },
    onSuccess: (t) => {
      onChanged();
      onClose();
      const what = panel.kind === 'logged' ? 'Saved' : hasActivity ? `${ACTIVITY[panel.type].label} logged` : 'Done';
      const undoIt =
        panel.kind === 'reminder'
          ? () => supabase.rpc('uncomplete_reminder', { p_reminder: panel.reminderId })
          : panel.kind === 'step'
            ? () => supabase.rpc('undo_next_step', { p_audit: panel.auditId })
            : null;
      toast({
        message: `${what}${t ? ` · ${ACTIVITY[t].label} reminder set` : ''}.`,
        autoHideMs: 8000,
        ...(undoIt ? { action: { label: 'Undo', onClick: () => void undoIt().then(onChanged) } } : {}),
      });
    },
  });

  const undo = useMutation({
    mutationFn: async () => {
      const { error } =
        panel.kind === 'reminder'
          ? await supabase.rpc('uncomplete_reminder', { p_reminder: panel.reminderId })
          : panel.kind === 'step'
            ? await supabase.rpc('undo_next_step', { p_audit: panel.auditId })
            : { error: null };
      rpcError(error);
    },
    onSuccess: () => {
      onChanged();
      onClose();
      toast({ message: 'Undone: it’s back on your list.', autoHideMs: 4000 });
    },
  });

  const title =
    panel.kind === 'logged'
      ? `${panel.type === 'other' ? 'Logged' : `${ACTIVITY[panel.type].label} logged`} · ${panel.who.name}`
      : hasActivity
        ? `${ACTIVITY[panel.type].label} logged · ${panel.who.name}`
        : `${ACTIVITY[panel.type].label} done · ${panel.who.name}`;

  return (
    <Modal title={title} open onClose={onClose}>
      <div className="space-y-4">
        {panel.kind !== 'logged' && !hasActivity && panel.type !== 'email' && (
          <p className="text-sm text-amber-700">There’s no contact on this, so nothing was added to a timeline.</p>
        )}

        {hasActivity && isCall && (
          <div>
            <div className="mb-1.5 text-sm font-medium text-slate-700">How did it go?</div>
            <div className="flex flex-wrap gap-1.5">
              {OUTCOMES.map((o) => (
                <Chip key={o.key} on={outcome === o.key} onClick={() => pickOutcome(o.key)}>
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
        )}

        {hasActivity && (
          <>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2} />
            <label className="block text-sm text-slate-600">
              When it happened
              <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1 !w-56" />
            </label>
          </>
        )}

        {panel.kind === 'logged' && panel.openReminders.length > 0 && (
          <div>
            <div className="mb-1.5 text-sm font-medium text-slate-700">Mark a reminder done too?</div>
            <div className="space-y-1 text-sm">
              {panel.openReminders.map((r) => (
                <label key={r.id} className="flex items-center gap-2">
                  <input type="radio" name="link-reminder" checked={linkTo === r.id} onChange={() => setLinkTo(r.id)} /> {r.note ?? 'Reminder'}
                </label>
              ))}
              <label className="flex items-center gap-2 text-slate-500">
                <input type="radio" name="link-reminder" checked={linkTo === null} onChange={() => setLinkTo(null)} /> No, leave it open
              </label>
            </div>
          </div>
        )}

        <div className="rounded-md bg-slate-50 p-3">
          <div className="mb-1.5 text-sm font-medium text-slate-700">Next task</div>
          <div className="flex flex-wrap gap-1.5">
            {ACTIVITY_TYPES.map((t) => (
              <Chip key={t} on={nextType === t} onClick={() => (setTouched(true), setNextType(t))}>
                {t === 'call' && panel.type === 'call' ? 'Call again' : t === 'meeting' ? 'Book meeting' : t === 'email' ? 'Send email' : 'Call'}
              </Chip>
            ))}
            <Chip on={nextType === null} onClick={() => (setTouched(true), setNextType(null))}>
              No follow-up
            </Chip>
          </div>
          {nextType && (
            <div className="mt-2.5 space-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {FOLLOW_UP_WHEN.map((w) => (
                  <Chip key={w.days} on={!nextDate && nextDays === w.days} onClick={() => (setTouched(true), setNextDate(''), setNextDays(w.days))}>
                    {w.label}
                  </Chip>
                ))}
                <Input type="date" value={nextDate} onChange={(e) => (setTouched(true), setNextDate(e.target.value))} className="!w-auto !py-0.5 text-xs" />
              </div>
              <Input value={nextNote} onChange={(e) => setNextNote(e.target.value)} placeholder={defaultNote(nextType, panel.who.name)} />
            </div>
          )}
        </div>

        <ErrorBox error={save.error ?? undo.error} />
        <div className="flex items-center justify-between">
          {panel.kind !== 'logged' ? (
            <button className="text-sm text-slate-500 hover:text-rose-600 hover:underline" disabled={undo.isPending} onClick={() => undo.mutate()}>
              Undo check-off
            </button>
          ) : (
            <span />
          )}
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Call / email / meeting icon for a reminder or next step (blank space for untyped ones). */
export function TypeIcon({ type }: { type: string | null }) {
  const a = type ? ACTIVITY[type as ActivityType] : null;
  if (!a) return <span className="mt-0.5 w-4 shrink-0" />;
  return <a.icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-label={a.label} />;
}
